import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useGroupLabels from 'hooks/useGroupLabels';
import MultiSelectFilter from 'components/admin/MultiSelectFilter';
import TeamColourDot from 'components/admin/TeamColourDot';
import { formatGroupCell } from 'utils/groupLabel';
import { SORT_BY } from 'utils/athleteOrder';
import style from './AthleteSeasonPointsTable.module.css';
import { formatPoints } from 'utils/points';

const AthleteSeasonPointsTable = ({
  athletePoints,
  setSearchText,
  minPRsBeaten,
  setMinPRsBeaten,
  sortBy,
  setSortBy,
  lastCalculatedAt,
  teamWords,
  teamOptions,
  teamFilter,
  setTeamFilter,
}) => {
  const [athletePointsTableData, setAthletePointsTableData] = useState([]);
  // The athlete's group column reads Age and "11", or Year and "Y6" for a club that groups by year level.
  const { groupingBasis, words } = useGroupLabels();

  // Column structure for athlete season points
  const columns = [
    { key: 'athleteFirstName', label: 'First Name' },
    { key: 'athleteLastName', label: 'Last Name' },
    { key: 'athleteAge', label: words.value },
    { key: 'athleteGender', label: 'Gender' },
    // A school's House column (points slice 5): the athlete's house now, in the school's word
    ...(teamWords ? [{ key: 'team', label: teamWords.team }] : []),
    { key: 'meetsParticipated', label: 'Meets' },
    { key: 'eventsParticipated', label: 'Events' },
    { key: 'personalBestsSet', label: 'SBs Set' },
    { key: 'personalBestsEqualed', label: 'SBs Equaled' },
    { key: 'personalBestsBeaten', label: 'SBs Beaten' },
    { key: 'firstPlaceFinishes', label: '1st' },
    { key: 'secondPlaceFinishes', label: '2nd' },
    { key: 'thirdPlaceFinishes', label: '3rd' },
    { key: 'totalPoints', label: 'Total Points' },
  ];

  const [sortKeys, setSortKeys] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = false;
      return acc;
    }, {})
  );

  useEffect(() => {
    setAthletePointsTableData(athletePoints);
  }, [athletePoints]);

  const handleAthleteSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleMinPRsBeatenChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setMinPRsBeaten(isNaN(value) || value < 0 ? 0 : value);
  };

  const sortTable = (key) => {
    // Create a copy to avoid mutating state directly
    const sortData = [...athletePointsTableData].sort((a, b) => {
      if (['athleteFirstName', 'athleteLastName', 'athleteGender', 'team'].includes(key)) {
        // String comparison; a house by its name, athletes with none together
        const textOf = (row) => (key === 'team' ? row.team?.name : row[key]);
        const aVal = (textOf(a) || '').toString().toLowerCase();
        const bVal = (textOf(b) || '').toString().toLowerCase();
        return sortKeys[key]
          ? bVal.localeCompare(aVal)
          : aVal.localeCompare(bVal);
      } else {
        // Numeric comparison
        return sortKeys[key] ? b[key] - a[key] : a[key] - b[key];
      }
    });
    
    setAthletePointsTableData(sortData);
    
    // Toggle sort direction
    setSortKeys({
      ...sortKeys,
      [key]: !sortKeys[key],
    });
  };

  const formatLastCalculated = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // The server sends UTC time without timezone indicator
      // Add 'Z' to explicitly mark it as UTC
      let utcDateString = dateString;
      if (!dateString.endsWith('Z') && !dateString.includes('+')) {
        utcDateString = dateString + 'Z';
      }
      
      // Parse the UTC date string
      const date = new Date(utcDateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      
      // Convert to local time and format
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric', // no leading zero with AM/PM: "3:37:55 PM", not "03:37:55 PM"
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  return (
    <div className={style.tableCard}>
      {/* Header with the time of the last points update */}
      <div className={style.tableHeader}>
        <div className={style.lastCalculated}>
          Points last updated: {formatLastCalculated(lastCalculatedAt)}
        </div>
      </div>

      {/* Search Bar */}
      <div className={style.searchBar}>
        <input
          type="text"
          className={style.searchInput}
          placeholder="Search athletes by name..."
          id="athleteSearch"
          onInput={handleAthleteSearch}
        />
        {teamWords && (
          <div className={style.filterGroup}>
            <span className={style.filterLabel}>{teamWords.team}:</span>
            <MultiSelectFilter
              value={teamFilter}
              options={teamOptions}
              onChange={setTeamFilter}
              allLabel={`All ${teamWords.lower.teams}`}
              itemLabel={teamWords.lower.team}
            />
          </div>
        )}
        <div className={style.filterGroup}>
          <label htmlFor="minPRsBeaten" className={style.filterLabel}>
            Min SBs Beaten:
          </label>
          <input
            type="number"
            id="minPRsBeaten"
            className={style.numberInput}
            value={minPRsBeaten}
            onChange={handleMinPRsBeatenChange}
            min="0"
            step="1"
          />
        </div>
        {/* Total points, or the athlete's group: youngest first, then female before male, then last
            name. The two files are exported in the order chosen here. */}
        <div className={style.filterGroup}>
          <label htmlFor="sortBy" className={style.filterLabel}>
            Sort by:
          </label>
          <select
            id="sortBy"
            className={style.selectInput}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value={SORT_BY.POINTS}>Total Points</option>
            <option value={SORT_BY.GROUP}>{words.group}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className={style.tableWrapper}>
        <table className={style.dataTable}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index} onClick={() => sortTable(col.key)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {athletePointsTableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6c757d',
                  }}
                >
                  No athlete season points data available
                </td>
              </tr>
            ) : (
              athletePointsTableData.map((point, index) => (
                <tr key={point.athletePointsID || index}>
                  <td className={style.athleteName}>{point.athleteFirstName}</td>
                  <td className={style.athleteName}>{point.athleteLastName}</td>
                  <td className={style.centered}>{formatGroupCell(groupingBasis, point.athleteAge)}</td>
                  <td className={style.centered}>{point.athleteGender}</td>
                  {teamWords && (
                    <td className={style.house}>
                      {point.team && (
                        <span className={style.houseInner}>
                          <TeamColourDot colour={point.team.colour} />
                          {point.team.name}
                        </span>
                      )}
                    </td>
                  )}
                  <td className={style.centered}>{point.meetsParticipated}</td>
                  <td className={style.centered}>{point.eventsParticipated}</td>
                  <td className={style.centered}>{point.personalBestsSet}</td>
                  <td className={style.centered}>{point.personalBestsEqualed}</td>
                  <td className={style.centered}>{point.personalBestsBeaten}</td>
                  <td className={style.centered}>{point.firstPlaceFinishes}</td>
                  <td className={style.centered}>{point.secondPlaceFinishes}</td>
                  <td className={style.centered}>{point.thirdPlaceFinishes}</td>
                  <td className={style.totalPoints}>{formatPoints(point.totalPoints)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

AthleteSeasonPointsTable.propTypes = {
  athletePoints: PropTypes.array,
  setSearchText: PropTypes.func,
  minPRsBeaten: PropTypes.number,
  setMinPRsBeaten: PropTypes.func,
  sortBy: PropTypes.string,
  setSortBy: PropTypes.func,
  lastCalculatedAt: PropTypes.string,
  // A school's words for a house (utils/teamLabel.js); null for a club, which has no House column or filter.
  teamWords: PropTypes.shape({
    team: PropTypes.string,
    lower: PropTypes.shape({ team: PropTypes.string, teams: PropTypes.string }),
  }),
  // The House filter's choices (teamFilterOptions) and its ticked values.
  teamOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  teamFilter: PropTypes.arrayOf(PropTypes.string),
  setTeamFilter: PropTypes.func,
};

export default AthleteSeasonPointsTable;